import { useCallback, type MutableRefObject, type RefObject } from "react";

import type { DocumentViewportValue } from "@/features/document-viewport/types";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";
import type { SnapSegment } from "@/features/snapping/types";
import {
  calculatePixelDistance,
  snapPointToIncrement,
  snapRectanglePointToIncrement,
  type Point,
} from "@/lib/geometry";
import type { DraftMeasurementState } from "../measurement-overlay-types";

const LOCKED_RAY_POINT_TOLERANCE_PX = 0.5;
const INTERSECTION_EPSILON = 0.000001;

function cross(aX: number, aY: number, bX: number, bY: number) {
  return aX * bY - aY * bX;
}

function isPointOnScreenRay(
  viewport: DocumentViewportValue,
  anchor: Point,
  through: Point,
  point: Point
) {
  const screenAnchor = viewport.transforms.pageToScreenPoint(anchor);
  const screenThrough = viewport.transforms.pageToScreenPoint(through);
  const screenPoint = viewport.transforms.pageToScreenPoint(point);
  const rayX = screenThrough.x - screenAnchor.x;
  const rayY = screenThrough.y - screenAnchor.y;
  const rayLengthSquared = rayX * rayX + rayY * rayY;
  if (rayLengthSquared === 0) {
    return false;
  }

  const pointX = screenPoint.x - screenAnchor.x;
  const pointY = screenPoint.y - screenAnchor.y;
  const projection = pointX * rayX + pointY * rayY;
  const perpendicularDistance =
    Math.abs(pointX * rayY - pointY * rayX) / Math.sqrt(rayLengthSquared);
  return (
    projection >= 0 &&
    perpendicularDistance <= LOCKED_RAY_POINT_TOLERANCE_PX
  );
}

function findScreenRaySegmentIntersection(
  viewport: DocumentViewportValue,
  anchor: Point,
  through: Point,
  fallbackPoint: Point,
  segment: SnapSegment
): Point | null {
  const rayStart = viewport.transforms.pageToScreenPoint(anchor);
  const rayThrough = viewport.transforms.pageToScreenPoint(through);
  const segmentStart = viewport.transforms.pageToScreenPoint({
    x: segment.x1,
    y: segment.y1,
  });
  const segmentEnd = viewport.transforms.pageToScreenPoint({
    x: segment.x2,
    y: segment.y2,
  });
  const rayX = rayThrough.x - rayStart.x;
  const rayY = rayThrough.y - rayStart.y;
  const segmentX = segmentEnd.x - segmentStart.x;
  const segmentY = segmentEnd.y - segmentStart.y;
  const denominator = cross(rayX, rayY, segmentX, segmentY);

  if (Math.abs(denominator) <= INTERSECTION_EPSILON) {
    return isPointOnScreenRay(viewport, anchor, through, fallbackPoint)
      ? fallbackPoint
      : null;
  }

  const offsetX = segmentStart.x - rayStart.x;
  const offsetY = segmentStart.y - rayStart.y;
  const rayT = cross(offsetX, offsetY, segmentX, segmentY) / denominator;
  const segmentT = cross(offsetX, offsetY, rayX, rayY) / denominator;
  if (rayT < 0 || segmentT < 0 || segmentT > 1) {
    return null;
  }

  return viewport.transforms.screenToPagePoint({
    x: rayStart.x + rayT * rayX,
    y: rayStart.y + rayT * rayY,
  });
}

interface UseMeasurementOverlayGeometryHelpersArgs {
  viewport: DocumentViewportValue;
  overlayRef: RefObject<HTMLDivElement | null>;
  latestDraftRef: MutableRefObject<DraftMeasurementState | null>;
  currentHeightFeet?: number;
  updateSnapHoverPoint: (
    pagePoint: Point | null,
    options?: {
      filter?: (point: Point) => boolean;
      resolveSegmentPoint?: (
        point: Point,
        segment: SnapSegment
      ) => Point | null;
    }
  ) => Point | null;
  incrementSnapEnabled: boolean;
  incrementStepFeet: number;
  scale: MeasurementScale | null;
}

export function useMeasurementOverlayGeometryHelpers({
  viewport,
  overlayRef,
  latestDraftRef,
  currentHeightFeet,
  updateSnapHoverPoint,
  incrementSnapEnabled,
  incrementStepFeet,
  scale,
}: UseMeasurementOverlayGeometryHelpersArgs) {
  const getPagePoint = useCallback(
    (clientX: number, clientY: number) => {
      const rect = overlayRef.current?.getBoundingClientRect();
      if (!rect) {
        return null;
      }
      return viewport.transforms.screenToPagePoint({
        x: clientX - rect.left,
        y: clientY - rect.top,
      });
    },
    [overlayRef, viewport.transforms]
  );

  const getScreenDistance = useCallback(
    (pointA: Point, pointB: Point) => {
      const screenA = viewport.transforms.pageToScreenPoint(pointA);
      const screenB = viewport.transforms.pageToScreenPoint(pointB);
      return calculatePixelDistance(screenA, screenB);
    },
    [viewport.transforms]
  );

  const getActiveHeight = useCallback(
    () => latestDraftRef.current?.height ?? currentHeightFeet,
    [currentHeightFeet, latestDraftRef]
  );

  const snapSegmentPoint = useCallback(
    (
      anchor: Point,
      candidate: Point,
      options?: {
        allowPdfSnap?: boolean;
        lockPdfSnapToCandidateRay?: boolean;
      }
    ) => {
      const incrementedPoint =
        incrementSnapEnabled && scale
          ? snapPointToIncrement(
              anchor,
              candidate,
              scale.pixelsPerFoot,
              incrementStepFeet,
              scale.pageAspectRatio
            )
          : candidate;
      const filter = options?.lockPdfSnapToCandidateRay
        ? (point: Point) =>
            isPointOnScreenRay(viewport, anchor, candidate, point)
        : undefined;
      const resolveSegmentPoint = options?.lockPdfSnapToCandidateRay
        ? (point: Point, segment: SnapSegment) =>
            findScreenRaySegmentIntersection(
              viewport,
              anchor,
              candidate,
              point,
              segment
            )
        : undefined;
      return options?.allowPdfSnap
        ? updateSnapHoverPoint(candidate, {
            filter,
            resolveSegmentPoint,
          }) ?? incrementedPoint
        : incrementedPoint;
    },
    [
      incrementSnapEnabled,
      incrementStepFeet,
      scale,
      updateSnapHoverPoint,
      viewport,
    ]
  );

  const snapRectangleCorner = useCallback(
    (
      anchor: Point,
      candidate: Point,
      lockSquare: boolean,
      options?: { allowPdfSnap?: boolean }
    ) => {
      const incrementedPoint =
        incrementSnapEnabled && scale
          ? snapRectanglePointToIncrement({
              anchor,
              point: candidate,
              pixelsPerFoot: scale.pixelsPerFoot,
              incrementFeet: incrementStepFeet,
              lockSquare,
              pageAspectRatio: scale.pageAspectRatio,
            })
          : candidate;
      return options?.allowPdfSnap
        ? updateSnapHoverPoint(candidate) ?? incrementedPoint
        : incrementedPoint;
    },
    [incrementSnapEnabled, incrementStepFeet, scale, updateSnapHoverPoint]
  );

  return {
    getPagePoint,
    getScreenDistance,
    getActiveHeight,
    snapSegmentPoint,
    snapRectangleCorner,
  };
}

export type { UseMeasurementOverlayGeometryHelpersArgs };
